import * as i0 from '@angular/core';
import { Injectable, EventEmitter, booleanAttribute, Directive, Self, Input, Output, HostListener, Component, ChangeDetectionStrategy, ViewEncapsulation, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i1 from '@niceltd/sol/text-input';
import { TextInputModule } from '@niceltd/sol/text-input';
import { CommonModule } from '@angular/common';
class TimeDurationService {
  constructor() {}
  validateTotalSeconds(totalSeconds, hoursDigitCount) {
    // if value is greater than 99 hours, 59 minutes, 59 seconds, set it to 99:59:59
    const maxHours = Math.pow(10, hoursDigitCount);
    if (totalSeconds >= maxHours * 3600) {
      totalSeconds = maxHours * 3600 - 1;
    }
    // if value is less than 0 seconds, set it to 0
    if (totalSeconds < 0) {
      totalSeconds = 0;
    }
    return totalSeconds;
  }
  getSeconds(hours, minutes, seconds) {
    return hours * 3600 + minutes * 60 + seconds;
  }
  secondsToDuration(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor(totalSeconds % 3600 / 60);
    const seconds = totalSeconds % 60;
    return {
      hours,
      minutes,
      seconds
    };
  }
  secondsToDurationData(totalSeconds, includeSeconds, separator, placeholder, hoursDigitCount) {
    totalSeconds = this.validateTotalSeconds(totalSeconds, hoursDigitCount);
    const duration = this.secondsToDuration(totalSeconds);
    const hours = duration.hours;
    const minutes = duration.minutes;
    const seconds = duration.seconds;
    let timeString = `${hours.toString().padStart(hoursDigitCount, placeholder)}${separator}${minutes.toString().padStart(2, placeholder)}`;
    if (includeSeconds) {
      timeString += `${separator}${seconds.toString().padStart(2, placeholder)}`;
    }
    return {
      timeString,
      totalSeconds,
      duration
    };
  }
  timeToDurationData(hours, minutes, seconds, includeSeconds, separator, placeholder, hoursDigitCount) {
    const totalSeconds = this.getSeconds(hours, minutes, seconds);
    return this.secondsToDurationData(totalSeconds, includeSeconds, separator, placeholder, hoursDigitCount);
  }
  static {
    this.ɵfac = function TimeDurationService_Factory(t) {
      return new (t || TimeDurationService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: TimeDurationService,
      factory: TimeDurationService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TimeDurationService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [], null);
})();
var TimeUnit;
(function (TimeUnit) {
  TimeUnit["HOURS"] = "hours";
  TimeUnit["MINUTES"] = "minutes";
  TimeUnit["SECONDS"] = "seconds";
})(TimeUnit || (TimeUnit = {}));
class TimeDurationDirective {
  static {
    this.validDigitsCodes = ['Digit0', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Numpad0', 'Numpad1', 'Numpad2', 'Numpad3', 'Numpad4', 'Numpad5', 'Numpad6', 'Numpad7', 'Numpad8', 'Numpad9'];
  }
  static {
    this.TIME_UNIT_VALUES = {
      [TimeUnit.HOURS]: 3600,
      [TimeUnit.MINUTES]: 60,
      [TimeUnit.SECONDS]: 1
    };
  }
  set timeDurationData(data) {
    this._currentTimeString = data.timeString;
    this._totalSeconds = data.totalSeconds;
    this._duration = data.duration;
    this._timeDurationData = data;
  }
  get timeDurationData() {
    return this._timeDurationData;
  }
  set totalSeconds(totalSeconds) {
    if (totalSeconds < 0 || totalSeconds === null || totalSeconds === undefined || typeof totalSeconds !== 'number' || isNaN(totalSeconds)) {
      totalSeconds = 0;
    }
    this.timeDurationData = this.timeService.secondsToDurationData(totalSeconds, this.includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
    this.renderAndSetSelection(this._currentTimeString, this._currentSelectedTimeUnit);
  }
  get totalSeconds() {
    return this._totalSeconds;
  }
  get hoursDigitCount() {
    return this._hoursDigitCount;
  }
  set hoursDigitCount(value) {
    if (value < 1 || value === null || value === undefined) {
      value = 1;
    }
    this.MAX_DURATION = {
      hours: Math.pow(10, value) - 1,
      minutes: 59,
      seconds: 59
    };
    this._hoursDigitCount = value;
    // Update the value of the input element
    this.totalSeconds = this._totalSeconds;
  }
  get includeSeconds() {
    return this._includeSeconds;
  }
  set includeSeconds(value) {
    this._includeSeconds = value;
    this.totalSeconds = this._totalSeconds;
  }
  constructor(_el, _renderer, timeService) {
    this._el = _el;
    this._renderer = _renderer;
    this.timeService = timeService;
    this.MAX_DURATION = {
      hours: 99,
      minutes: 59,
      seconds: 59
    };
    this.MIN_DURATION = {
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    /**
     * This variable indicates that the block (hours or minutes or seconds) should behave as
     * if it had just received focus
     */
    this._fieldJustGotFocus = false;
    this._currentSelectedTimeUnit = TimeUnit.HOURS;
    this._placeholder = '0';
    this._separator = ':';
    this._includeSeconds = true;
    this._hoursDigitCount = 2;
    this._totalSeconds = 0;
    this._duration = {
      hours: 0,
      minutes: 0,
      seconds: 0
    };
    this._timeDurationData = this.timeService.secondsToDurationData(this._totalSeconds, this._includeSeconds, this._separator, this._placeholder, this._hoursDigitCount);
    this._currentTimeString = this.timeDurationData.timeString;
    this.readonly = false;
    this.totalSecondsChange = new EventEmitter();
  }
  ngAfterViewInit() {
    this.getInputElement().style.cursor = 'default';
    // add class to element
    this._renderer.addClass(this._el.nativeElement, 'sol-time-duration');
    // add class to input element
    this._renderer.addClass(this.getInputElement(), 'sol-time-duration-input');
    this._renderer.setAttribute(this.getInputElement(), 'aria-live', 'polite');
    this.totalSeconds = this._totalSeconds;
  }
  /** When the component receives a click, you need to select hours or minutes or seconds */
  onClick(evt) {
    if (this.readonly || evt.target.tagName.toUpperCase() !== 'INPUT') {
      return;
    }
    this._fieldJustGotFocus = true;
    this.highlightCurrentTimeUnit();
  }
  /** When the component receives focus, you must select hours or minutes or seconds  */
  onFocus(evt) {
    if (this.readonly || evt.target.tagName.toUpperCase() !== 'INPUT') {
      return;
    }
    this._fieldJustGotFocus = true;
    this.highlightCurrentTimeUnit();
  }
  onEvent(event) {
    event.preventDefault();
  }
  /** Checks for keys */
  onKeyDown(evt) {
    if (this.readonly || evt.target.tagName.toUpperCase() !== 'INPUT') {
      return;
    }
    const keyCode = evt.code;
    switch (keyCode) {
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Tab':
        this.decideWhetherToJumpAndSelect(evt);
        break;
      case 'Delete':
      case 'Backspace':
        evt.preventDefault();
        this.clearHoursOrMinutesOrSeconds();
        break;
      case 'ArrowUp':
        this.modifyTime(true);
        break;
      case 'ArrowDown':
        this.modifyTime(false);
        break;
      default:
        if (TimeDurationDirective.validDigitsCodes.includes(keyCode)) {
          // deals with numbers
          this.setInputText(evt.key);
        }
    }
    // prevents the component from trying to update itself:
    // 1 - When the user types a number, it would cause the screen to blink: once because we are
    //      changing the component value and once as a default response to the user typing
    // 2 - When the user types a key other than those discussed above, it must be ignored
    if (keyCode !== 'Tab') {
      evt.preventDefault();
    }
  }
  getInputElement() {
    return this._el.nativeElement.querySelector('input');
  }
  setValueInDOM(value) {
    this._renderer.setProperty(this.getInputElement(), 'value', value);
  }
  renderAndSetSelection(newTimeString, timeUnit) {
    this.setValueInDOM(newTimeString);
    this.setSelectionOnTimeUnit(timeUnit);
  }
  highlightCurrentTimeUnit() {
    const caretPosition = this._doGetCaretPosition();
    if (caretPosition < this.hoursDigitCount + 1) {
      this.setSelectionOnHours();
    } else if (caretPosition < this.hoursDigitCount + 4) {
      this.setSelectionOnMinutes();
    } else {
      this.setSelectionOnSeconds();
    }
  }
  setSelectionOnTimeUnit(timeUnit) {
    switch (timeUnit) {
      case TimeUnit.HOURS:
        this.setSelectionOnHours();
        break;
      case TimeUnit.MINUTES:
        this.setSelectionOnMinutes();
        break;
      case TimeUnit.SECONDS:
        this.setSelectionOnSeconds();
        break;
    }
  }
  setSelectionOnHours() {
    this.getInputElement().setSelectionRange(0, this.hoursDigitCount);
    this._currentSelectedTimeUnit = TimeUnit.HOURS;
  }
  setSelectionOnMinutes() {
    this.getInputElement().setSelectionRange(this.hoursDigitCount + 1, this.hoursDigitCount + 3);
    this._currentSelectedTimeUnit = TimeUnit.MINUTES;
  }
  setSelectionOnSeconds() {
    if (this.includeSeconds) {
      this.getInputElement().setSelectionRange(this.hoursDigitCount + 4, this.hoursDigitCount + 6);
      this._currentSelectedTimeUnit = TimeUnit.SECONDS;
    } else {
      this.setSelectionOnMinutes();
    }
  }
  getTimeUnitChangeValue(selectedTimeUnit, duration, isIncrement) {
    const limitDuration = isIncrement ? this.MAX_DURATION : this.MIN_DURATION;
    if (selectedTimeUnit === TimeUnit.HOURS && duration.hours === limitDuration.hours) {
      return 0;
    }
    if (selectedTimeUnit === TimeUnit.MINUTES && duration.hours === limitDuration.hours && duration.minutes === limitDuration.minutes) {
      return 0;
    }
    if (selectedTimeUnit === TimeUnit.SECONDS && duration.hours === limitDuration.hours && duration.minutes === limitDuration.minutes && duration.seconds === limitDuration.seconds) {
      return 0;
    }
    return TimeDurationDirective.TIME_UNIT_VALUES[selectedTimeUnit];
  }
  // Modify the time by one second or 1 minute or 1 hour when arrow up or down is pressed
  modifyTime(increase) {
    const currentTimeUnit = this._currentSelectedTimeUnit;
    let totalSeconds = this._totalSeconds;
    const duration = this._duration;
    // Determine the increment or decrement based on the currently selected block (hours, minutes, or seconds)
    const change = this.getTimeUnitChangeValue(currentTimeUnit, duration, increase);
    // Modify the total seconds and convert back to a time string
    totalSeconds = increase ? totalSeconds + change : totalSeconds - change;
    const newData = this.timeService.secondsToDurationData(totalSeconds, this.includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
    // Update the value of the input element
    this.updateAndRenderValue(newData, currentTimeUnit);
  }
  decideWhetherToJumpAndSelect(evt) {
    const currentTimeUnit = this._currentSelectedTimeUnit;
    const keyCode = evt.code;
    let direction = 'ArrowRight';
    if (keyCode === 'ArrowLeft' || keyCode === 'Tab' && evt.shiftKey) {
      direction = 'ArrowLeft';
    }
    let canLeave;
    switch (direction) {
      case 'ArrowRight':
        canLeave = currentTimeUnit === TimeUnit.SECONDS || currentTimeUnit === TimeUnit.MINUTES && !this.includeSeconds;
        if (!canLeave) {
          evt.preventDefault(); // prevent TAB from leaving input unless the focus is in the last block
        }
        switch (currentTimeUnit) {
          case TimeUnit.HOURS:
            this.setSelectionOnMinutes();
            break;
          case TimeUnit.MINUTES:
          case TimeUnit.SECONDS:
            this.setSelectionOnSeconds();
            break;
        }
        break;
      case 'ArrowLeft':
        canLeave = currentTimeUnit === TimeUnit.HOURS;
        if (!canLeave) {
          evt.preventDefault(); // prevent SHIFT TAB from leaving input unless the focus is in the first block
        }
        switch (currentTimeUnit) {
          case TimeUnit.HOURS:
          case TimeUnit.MINUTES:
            this.setSelectionOnHours();
            break;
          case TimeUnit.SECONDS:
            this.setSelectionOnMinutes();
            break;
        }
        break;
    }
    this._fieldJustGotFocus = true;
  }
  /**
   * Method called when the user types a number key
   */
  setInputText(key) {
    const input = this._currentTimeString.split(this._separator);
    const hours = input[0];
    const minutes = input[1];
    const seconds = this.includeSeconds ? input[2] : '0';
    const currentTimeUnit = this._currentSelectedTimeUnit;
    let newTimeUnit = currentTimeUnit;
    let newData = this.timeDurationData;
    switch (currentTimeUnit) {
      case TimeUnit.HOURS:
        [newData, newTimeUnit] = this.setHours(hours, minutes, seconds, key);
        break;
      case TimeUnit.MINUTES:
        [newData, newTimeUnit] = this.setMinutes(hours, minutes, seconds, key);
        break;
      case TimeUnit.SECONDS:
        [newData, newTimeUnit] = this.setSeconds(hours, minutes, seconds, key);
        break;
    }
    this.updateAndRenderValue(newData, newTimeUnit);
  }
  setHours(hours, minutes, seconds, key) {
    let sendCaretToMinutes = false;
    let newHourInt = 0;
    // logic for hour field when the field just got focus and when to send caret to minutes
    if (this.hoursDigitCount === 1 || this._fieldJustGotFocus) {
      newHourInt = Number(key);
      this._fieldJustGotFocus = false;
      // Move caret to minutes if hoursDigitCount is 1 and user types a number > 0.
      sendCaretToMinutes = this.hoursDigitCount === 1 && !!newHourInt;
    } else {
      newHourInt = Number(hours) * 10 + Number(key);
      sendCaretToMinutes = newHourInt.toString().length === this.hoursDigitCount;
    }
    const newData = this.timeService.timeToDurationData(newHourInt, Number(minutes), Number(seconds), this._includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
    const newTimeUnit = sendCaretToMinutes ? TimeUnit.MINUTES : TimeUnit.HOURS;
    if (sendCaretToMinutes) {
      this._fieldJustGotFocus = true;
    }
    return [newData, newTimeUnit];
  }
  setMinutes(hours, minutes, seconds, key) {
    let newMinutes = '';
    let sendCaretToSeconds = false;
    if (this._fieldJustGotFocus) {
      newMinutes = `0${key}`;
      sendCaretToSeconds = this.includeSeconds && Number(key) > 5;
      this._fieldJustGotFocus = false;
    } else {
      if (Number(minutes) >= 10) {
        newMinutes = `0${key}`;
      } else {
        newMinutes = `${minutes[1]}${key}`;
        if (Number(newMinutes) > 59) {
          newMinutes = `0${key}`;
        }
      }
      sendCaretToSeconds = this.includeSeconds;
    }
    const newData = this.timeService.timeToDurationData(Number(hours), Number(newMinutes), Number(seconds), this._includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
    const newTimeUnit = sendCaretToSeconds ? TimeUnit.SECONDS : TimeUnit.MINUTES;
    if (sendCaretToSeconds) {
      this._fieldJustGotFocus = true;
    }
    return [newData, newTimeUnit];
  }
  setSeconds(hours, minutes, seconds, key) {
    let newSeconds = '';
    if (this._fieldJustGotFocus) {
      newSeconds = `0${key}`;
      this._fieldJustGotFocus = false;
    } else if (Number(seconds) >= 10) {
      newSeconds = `0${key}`;
    } else {
      newSeconds = `${seconds[1]}${key}`;
      if (Number(newSeconds) > 59) {
        newSeconds = `0${key}`;
      }
    }
    const newData = this.timeService.timeToDurationData(Number(hours), Number(minutes), Number(newSeconds), this._includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
    return [newData, TimeUnit.SECONDS];
  }
  /** Handles the backspace or delete key event */
  clearHoursOrMinutesOrSeconds() {
    const input = this._currentTimeString.split(this._separator);
    const hours = input[0];
    const minutes = input[1];
    const seconds = this.includeSeconds ? input[2] : '0';
    this._fieldJustGotFocus = true;
    let newData;
    switch (this._currentSelectedTimeUnit) {
      case TimeUnit.HOURS:
        newData = this.timeService.timeToDurationData(this.MIN_DURATION.hours, Number(minutes), Number(seconds), this.includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
        break;
      case TimeUnit.MINUTES:
        newData = this.timeService.timeToDurationData(Number(hours), this.MIN_DURATION.minutes, Number(seconds), this.includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
        break;
      case TimeUnit.SECONDS:
        newData = this.timeService.timeToDurationData(Number(hours), Number(minutes), this.MIN_DURATION.seconds, this.includeSeconds, this._separator, this._placeholder, this.hoursDigitCount);
        break;
    }
    this.updateAndRenderValue(newData, this._currentSelectedTimeUnit);
  }
  /*
   ** Returns the caret (cursor) position of the specified text field.
   ** Return value range is 0-nativeElement.value.length.
   */
  _doGetCaretPosition() {
    // Initialize
    let iCaretPos = 0;
    const nativeElement = this.getInputElement();
    // IE Support
    if (document.hasOwnProperty('selection')) {
      // Set focus on the element
      nativeElement.focus();
      // To get cursor position, get empty selection range
      const oSel = document['selection'].createRange();
      // Move selection start to 0 position
      oSel.moveStart('character', -nativeElement.value.length);
      // The caret position is selection length
      iCaretPos = oSel.text.length;
    } else if (nativeElement.selectionStart || nativeElement.selectionStart === 0) {
      // Firefox support
      iCaretPos = nativeElement.selectionStart;
    }
    // Return results
    return iCaretPos;
  }
  updateAndRenderValue(data, timeUnit) {
    this.renderAndSetSelection(data.timeString, timeUnit);
    if (data.totalSeconds !== this._totalSeconds) {
      this.timeDurationData = data;
      this.totalSecondsChange.emit(this._totalSeconds);
    }
  }
  static {
    this.ɵfac = function TimeDurationDirective_Factory(t) {
      return new (t || TimeDurationDirective)(i0.ɵɵdirectiveInject(i0.ElementRef, 2), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(TimeDurationService));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: TimeDurationDirective,
      selectors: [["", "solTimeDuration", ""]],
      hostBindings: function TimeDurationDirective_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function TimeDurationDirective_click_HostBindingHandler($event) {
            return ctx.onClick($event);
          })("focusin", function TimeDurationDirective_focusin_HostBindingHandler($event) {
            return ctx.onFocus($event);
          })("cut", function TimeDurationDirective_cut_HostBindingHandler($event) {
            return ctx.onEvent($event);
          })("paste", function TimeDurationDirective_paste_HostBindingHandler($event) {
            return ctx.onEvent($event);
          })("drop", function TimeDurationDirective_drop_HostBindingHandler($event) {
            return ctx.onEvent($event);
          })("keydown", function TimeDurationDirective_keydown_HostBindingHandler($event) {
            return ctx.onKeyDown($event);
          });
        }
      },
      inputs: {
        readonly: "readonly",
        totalSeconds: "totalSeconds",
        hoursDigitCount: "hoursDigitCount",
        includeSeconds: [i0.ɵɵInputFlags.HasDecoratorInputTransform, "includeSeconds", "includeSeconds", booleanAttribute]
      },
      outputs: {
        totalSecondsChange: "totalSecondsChange"
      },
      features: [i0.ɵɵInputTransformsFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TimeDurationDirective, [{
    type: Directive,
    args: [{
      selector: '[solTimeDuration]'
    }]
  }], () => [{
    type: i0.ElementRef,
    decorators: [{
      type: Self
    }]
  }, {
    type: i0.Renderer2
  }, {
    type: TimeDurationService
  }], {
    readonly: [{
      type: Input
    }],
    totalSeconds: [{
      type: Input
    }],
    hoursDigitCount: [{
      type: Input
    }],
    includeSeconds: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    totalSecondsChange: [{
      type: Output
    }],
    onClick: [{
      type: HostListener,
      args: ['click', ['$event']]
    }],
    onFocus: [{
      type: HostListener,
      args: ['focusin', ['$event']]
    }],
    onEvent: [{
      type: HostListener,
      args: ['cut', ['$event']]
    }, {
      type: HostListener,
      args: ['paste', ['$event']]
    }, {
      type: HostListener,
      args: ['drop', ['$event']]
    }],
    onKeyDown: [{
      type: HostListener,
      args: ['keydown', ['$event']]
    }]
  });
})();
let timeDurationInputCount = 1;
class TimeDurationComponent {
  constructor(cdr) {
    this.cdr = cdr;
    this.labelText = '';
    this.disabled = false;
    this.name = '';
    this.id = `sol-time-duration-${timeDurationInputCount}`;
    this.readonly = false;
    this.errorMessageText = null;
    this.ariaLabel = '';
    this.ariaLabelledby = null;
    this.leadingIcon = null;
    this.trailingIcon = null;
    this.errorState = false;
    this.totalSeconds = 0;
    this.includeSeconds = true;
    this.hoursDigitCount = 2;
    this.totalSecondsChange = new EventEmitter();
    this.changeFn = _ => {};
    this.touchedFn = () => {};
    timeDurationInputCount++;
  }
  writeValue(value) {
    if (value === null || value === undefined) {
      value = 0;
    } else if (typeof value !== 'number' || isNaN(value)) {
      value = 0;
    } else {
      value = Math.floor(value);
    }
    this.totalSeconds = value;
  }
  registerOnChange(fn) {
    this.changeFn = fn;
  }
  registerOnTouched(fn) {
    this.touchedFn = fn;
  }
  setDisabledState(isDisabled) {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }
  onTotalSecondsChange(seconds) {
    this.totalSeconds = seconds;
    this.changeFn(seconds);
    this.totalSecondsChange.emit(seconds);
  }
  onFocusOut() {
    this.touchedFn();
  }
  static {
    this.ɵfac = function TimeDurationComponent_Factory(t) {
      return new (t || TimeDurationComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: TimeDurationComponent,
      selectors: [["sol-time-duration"]],
      inputs: {
        labelText: "labelText",
        disabled: "disabled",
        name: "name",
        id: "id",
        readonly: "readonly",
        errorMessageText: "errorMessageText",
        ariaLabel: [i0.ɵɵInputFlags.None, "aria-label", "ariaLabel"],
        ariaLabelledby: [i0.ɵɵInputFlags.None, "aria-labelledby", "ariaLabelledby"],
        leadingIcon: "leadingIcon",
        trailingIcon: "trailingIcon",
        errorState: "errorState",
        totalSeconds: "totalSeconds",
        includeSeconds: "includeSeconds",
        hoursDigitCount: "hoursDigitCount"
      },
      outputs: {
        totalSecondsChange: "totalSecondsChange"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: TimeDurationComponent,
        multi: true
      }])],
      decls: 1,
      vars: 14,
      consts: [["autocomplete", "off", "placeholderText", "00:00:00", "solTimeDuration", "", 3, "labelText", "name", "id", "disabled", "readonly", "aria-label", "aria-labelledby", "leadingIcon", "trailingIcon", "errorState", "errorMessageText", "includeSeconds", "hoursDigitCount", "totalSeconds", "focusout", "totalSecondsChange"]],
      template: function TimeDurationComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "sol-text-input", 0);
          i0.ɵɵlistener("focusout", function TimeDurationComponent_Template_sol_text_input_focusout_0_listener() {
            return ctx.onFocusOut();
          })("totalSecondsChange", function TimeDurationComponent_Template_sol_text_input_totalSecondsChange_0_listener($event) {
            return ctx.onTotalSecondsChange($event);
          });
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵproperty("labelText", ctx.labelText)("name", ctx.name)("id", ctx.id)("disabled", ctx.disabled)("readonly", ctx.readonly)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledby)("leadingIcon", ctx.leadingIcon)("trailingIcon", ctx.trailingIcon)("errorState", ctx.errorState)("errorMessageText", ctx.errorMessageText)("includeSeconds", ctx.includeSeconds)("hoursDigitCount", ctx.hoursDigitCount)("totalSeconds", ctx.totalSeconds);
        }
      },
      dependencies: [i1.TextInputComponent, TimeDurationDirective],
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TimeDurationComponent, [{
    type: Component,
    args: [{
      selector: 'sol-time-duration',
      template: `
    <sol-text-input
      [labelText]="labelText"
      [name]="name"
      [id]="id"
      [disabled]="disabled"
      [readonly]="readonly"
      autocomplete="off"
      placeholderText="00:00:00"
      [aria-label]="ariaLabel"
      [aria-labelledby]="ariaLabelledby"
      [leadingIcon]="leadingIcon"
      [trailingIcon]="trailingIcon"
      [errorState]="errorState"
      [errorMessageText]="errorMessageText"
      (focusout)="onFocusOut()"
      solTimeDuration
      [includeSeconds]="includeSeconds"
      [hoursDigitCount]="hoursDigitCount"
      [totalSeconds]="totalSeconds"
      (totalSecondsChange)="onTotalSecondsChange($event)"
    ></sol-text-input>
  `,
      changeDetection: ChangeDetectionStrategy.OnPush,
      encapsulation: ViewEncapsulation.None,
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: TimeDurationComponent,
        multi: true
      }]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }], {
    labelText: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    name: [{
      type: Input
    }],
    id: [{
      type: Input
    }],
    readonly: [{
      type: Input
    }],
    errorMessageText: [{
      type: Input
    }],
    ariaLabel: [{
      type: Input,
      args: ['aria-label']
    }],
    ariaLabelledby: [{
      type: Input,
      args: ['aria-labelledby']
    }],
    leadingIcon: [{
      type: Input
    }],
    trailingIcon: [{
      type: Input
    }],
    errorState: [{
      type: Input
    }],
    totalSeconds: [{
      type: Input
    }],
    includeSeconds: [{
      type: Input
    }],
    hoursDigitCount: [{
      type: Input
    }],
    totalSecondsChange: [{
      type: Output
    }]
  });
})();
class TimeDurationModule {
  static {
    this.ɵfac = function TimeDurationModule_Factory(t) {
      return new (t || TimeDurationModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: TimeDurationModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [TimeDurationService],
      imports: [CommonModule, TextInputModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TimeDurationModule, [{
    type: NgModule,
    args: [{
      declarations: [TimeDurationDirective, TimeDurationComponent],
      imports: [CommonModule, TextInputModule],
      exports: [TimeDurationDirective, TimeDurationComponent],
      providers: [TimeDurationService]
    }]
  }], null, null);
})();

/*
 * Public API Surface of time duration
 */

/**
 * Generated bundle index. Do not edit.
 */

export { TimeDurationComponent, TimeDurationDirective, TimeDurationModule, TimeDurationService, TimeUnit };
